import React from 'react';
import { FunctionComponent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { agendaItemSelectors } from '../../../../../app/resource/agenda-item/agenda-item.selectors';
import { boardSelectors } from '../../../../../app/resource/boards/board/board.selectors';
import { boardListSelectors } from '../../../../../app/resource/boards/boardList/boardList.selectors';

import { useRequiredValidation } from '../../../../../utils/form/validation.utils';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { SelectInput } from '../../inputs/SelectInput';

import { FormTextInput } from '../../inputs/TextInput';
import { IAgendaCustomItemCreateForm, IAgendaCustomItemFormProps } from './types';
import { FormCSTextInput } from '../../inputs/CustomSizeTextInput';
import { agendaItemsPageSelectors } from '../../../../../app/page/page-storage/agenda-items/agenda-items.selectors';
import { IAgendaItem } from '../../../../../app/resource/agenda-item/agenda-item.types';
import { saveAgendaItems } from '../../../../../app/page/page-storage/agenda-items/agenda-items.actions';

export const AgendaCustomItemForm: FunctionComponent<IAgendaCustomItemFormProps> = ({ meetingId, boardIds }) => {
  const dispatch = useDispatch();

  const formProps = useForm();

  const requiredValidation = useRequiredValidation();
  const boardOptions = useSelectWithParams(boardSelectors.selectResourcesById, boardIds).map((board) => ({
    value: board?.id,
    label: board?.name,
  }));
  const agendaItemIds = useSelect(agendaItemsPageSelectors.selectAgendaItems);
  const agendaItemsSelected = useSelectWithParams(
    agendaItemSelectors.selectResourcesById,
    agendaItemIds[meetingId],
  ) as IAgendaItem[];

  const onSubmit = (data: IAgendaCustomItemCreateForm) => {
    const newAgendaItem = {
      title: data.name,
      boardId: data.board?.value ? data.board?.value : '',
      boardListId: data.boardList?.value ? data.boardList?.value : '',
      embedUrl: data.embedUrl ? data.embedUrl : '',
      checked: '1',
      duration: data.duration,
      description: '',
      sort: `${agendaItemsSelected.length - 1}`,
    } as IAgendaItem;

    const newAgendaItems = [
      ...agendaItemsSelected.slice(0, agendaItemsSelected.length - 1),
      newAgendaItem,
      ...agendaItemsSelected.slice(agendaItemsSelected.length - 1),
    ];
    const agendaItemsSorted = newAgendaItems.map((ai: IAgendaItem, index: number) => ({
      ...ai,
      sort: `${index}`,
    }));
    dispatch(saveAgendaItems(meetingId, agendaItemsSorted));
  };

  const focusConnectTuple = formProps.watch('control');
  const focusBoardTuple = formProps.watch('board');
  const boardListOptions = useSelect(boardListSelectors.selectResourceList)
    .filter((boardList) => focusBoardTuple && boardList.boardId === focusBoardTuple.value)
    .map((boardList) => ({ value: boardList.id, label: boardList.name }));

  const connectOptions = [
    { value: '0', label: 'Accountability Board' },
    { value: '1', label: 'Accountability List' },
    { value: '2', label: 'External URL' },
    { value: '3', label: `Meeting Board` },
  ];

  return (
    <FormProvider {...formProps}>
      <form
        id="agendaCustomItem-form"
        className="topic-form"
        onSubmit={formProps.handleSubmit((formData) => onSubmit(formData as IAgendaCustomItemCreateForm))}
        method="post"
      >
        <div className="field-container">
          <FormTextInput
            id="name"
            name="name"
            type="text"
            label="Title"
            placeholder="Enter agenda item name"
            // defaultValue={boardListData ? boardListData.name : ''}
            validation={requiredValidation}
          />
        </div>
        <div className="field-container">
          <SelectInput
            id="control"
            name="control"
            label="Connect to"
            // defaultOptions={selectedUser}
            validation={requiredValidation}
            options={connectOptions}
            isSingle
          />
        </div>
        {focusConnectTuple && (focusConnectTuple.value === '0' || focusConnectTuple.value === '1') && (
          <div className="field-container">
            <SelectInput
              id="board"
              name="board"
              label="Board"
              // defaultOptions={selectedUser}
              validation={requiredValidation}
              options={boardOptions}
              isSingle
            />
          </div>
        )}
        {focusConnectTuple && focusConnectTuple.value === '1' && (
          <div className="field-container">
            <SelectInput
              id="boardList"
              name="boardList"
              label="Board List"
              // defaultOptions={selectedUser}
              validation={requiredValidation}
              options={boardListOptions}
              isSingle
            />
          </div>
        )}
        {focusConnectTuple && focusConnectTuple.value === '2' && (
          <div className="field-container">
            <FormTextInput
              id="embedUrl"
              name="embedUrl"
              type="text"
              label="Embed URL"
              placeholder="Enter"
              // defaultValue={boardListData ? boardListData.name : ''}
              validation={requiredValidation}
            />
          </div>
        )}
        <div className="field-container">
          <FormCSTextInput
            id="duration"
            name="duration"
            type="text"
            size="mw"
            label="Duration"
            placeholder="Minutes"
            validation={requiredValidation}
          />
        </div>
      </form>
    </FormProvider>
  );
};
